import { Box, Typography } from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

// Guía visual genérica (sin datos reales de nadie) de cómo copiar el header
// Cookie desde las herramientas de desarrollador. Son mockups dibujados con
// CSS, no capturas reales — así no dependen de ningún archivo/imagen externo
// y no muestran la sesión de ninguna persona.

function StepFrame({ n, title, children }) {
  return (
    <Box sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: "50%",
          bgcolor: "primary.main",
          color: "#fff",
          display: "grid",
          placeItems: "center",
          fontSize: 12.5,
          fontWeight: 800,
          mt: 0.2,
        }}
      >
        {n}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.8 }}>{title}</Typography>
        {children}
      </Box>
    </Box>
  );
}

function MockFrame({ children }) {
  return (
    <Box
      sx={(theme) => ({
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.12)",
        bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#fff",
      })}
    >
      {children}
    </Box>
  );
}

function BrowserTopBar({ url }) {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 0.6,
        px: 1,
        py: 0.7,
        bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
        borderBottom: "1px solid",
        borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
      })}
    >
      {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
        <Box key={c} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c }} />
      ))}
      <Box
        sx={(theme) => ({
          ml: 1,
          flex: 1,
          fontSize: 10.5,
          fontFamily: "monospace",
          color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.6)" : "text.secondary",
          bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
          borderRadius: 1,
          px: 1,
          py: 0.3,
        })}
      >
        {url}
      </Box>
    </Box>
  );
}

export default function CookieCaptureGuide() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.4 }}>
      <StepFrame n={1} title="Entrá al portal judicial y logueate con este abogado">
        <MockFrame>
          <BrowserTopBar url="justiciacordoba.gob.ar/.../extranet.aspx" />
          <Box sx={{ p: 1.6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 11.5, opacity: 0.6 }}>SERVICIOS EXTRANET</Typography>
            <Box
              sx={{
                mt: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.2,
                py: 0.4,
                borderRadius: 999,
                bgcolor: "rgba(46,160,67,0.14)",
                color: "#2ea043",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              ● Sesión iniciada
            </Box>
          </Box>
        </MockFrame>
      </StepFrame>

      <StepFrame n={2} title="Abrí las herramientas de desarrollador (tecla F12) — antes o después de loguearte, da igual">
        <MockFrame>
          <BrowserTopBar url="justiciacordoba.gob.ar/.../extranet.aspx" />
          <Box sx={{ display: "flex" }}>
            <Box sx={{ flex: 1.3, p: 1.2 }}>
              <Typography sx={{ fontSize: 10.5, opacity: 0.5 }}>(la página)</Typography>
            </Box>
            <Box
              sx={(theme) => ({
                flex: 1,
                borderLeft: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.015)",
                p: 1,
              })}
            >
              <Box
                sx={{
                  border: "1.5px solid",
                  borderColor: "primary.main",
                  borderRadius: 1,
                  px: 0.8,
                  py: 0.3,
                  display: "inline-block",
                  fontSize: 10,
                  fontWeight: 800,
                  fontFamily: "monospace",
                }}
              >
                F12
              </Box>
              <Typography sx={{ fontSize: 10, opacity: 0.55, mt: 0.6 }}>
                → se abre este panel a la derecha. Dejalo abierto.
              </Typography>
            </Box>
          </Box>
        </MockFrame>
      </StepFrame>

      <StepFrame n={3} title="Recargá la página (tecla F5) — recién ahí aparecen los pedidos">
        <MockFrame>
          <Box
            sx={(theme) => ({
              display: "flex",
              gap: 1.4,
              px: 1,
              py: 0.7,
              borderBottom: "1px solid",
              borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
              fontSize: 10.5,
              overflowX: "auto",
            })}
          >
            {["Elements", "Console", "Sources"].map((t) => (
              <Typography key={t} sx={{ fontSize: 10.5, opacity: 0.4, whiteSpace: "nowrap" }}>
                {t}
              </Typography>
            ))}
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 800,
                color: "primary.main",
                borderBottom: "2px solid",
                borderColor: "primary.main",
                whiteSpace: "nowrap",
              }}
            >
              Network
            </Typography>
            <Typography sx={{ fontSize: 10.5, opacity: 0.4, whiteSpace: "nowrap" }}>Performance</Typography>
          </Box>
          <Box sx={{ p: 1.2 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                px: 1,
                py: 0.3,
                borderRadius: 999,
                border: "1.5px solid",
                borderColor: "primary.main",
                fontSize: 10,
                fontWeight: 800,
                fontFamily: "monospace",
                mb: 0.8,
              }}
            >
              F5
            </Box>
            <Typography sx={{ fontSize: 10.5, opacity: 0.6 }}>
              Esta lista arranca vacía siempre que recién abriste el panel — es normal.
              Recargá la página y ya aparecen los pedidos acá.
            </Typography>
          </Box>
        </MockFrame>
      </StepFrame>

      <StepFrame n={4} title='Click en cualquier pedido a la página (ej. "extranet.aspx")'>
        <MockFrame>
          <Box sx={{ display: "flex" }}>
            <Box
              sx={(theme) => ({
                flex: 1,
                borderRight: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
              })}
            >
              {["jquery.min.js", "extranet.aspx", "bootstrap.min.css", "api.js"].map((name) => (
                <Box
                  key={name}
                  sx={(theme) => ({
                    px: 1,
                    py: 0.55,
                    fontSize: 10.5,
                    fontFamily: "monospace",
                    ...(name === "extranet.aspx"
                      ? {
                          bgcolor: theme.palette.mode === "dark" ? "rgba(25,118,210,0.25)" : "rgba(25,118,210,0.12)",
                          fontWeight: 800,
                          color: "primary.main",
                        }
                      : { opacity: 0.55 }),
                  })}
                >
                  {name}
                </Box>
              ))}
            </Box>
          </Box>
        </MockFrame>
      </StepFrame>

      <StepFrame n={5} title='En "Headers" → "Request Headers", copiá el valor completo de "Cookie"'>
        <MockFrame>
          <Box
            sx={(theme) => ({
              display: "flex",
              gap: 1.4,
              px: 1,
              py: 0.6,
              borderBottom: "1px solid",
              borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
              fontSize: 10.5,
            })}
          >
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "primary.main" }}>Headers</Typography>
            <Typography sx={{ fontSize: 10.5, opacity: 0.4 }}>Preview</Typography>
            <Typography sx={{ fontSize: 10.5, opacity: 0.4 }}>Response</Typography>
          </Box>
          <Box sx={{ p: 1 }}>
            <Typography sx={{ fontSize: 10, opacity: 0.5, mb: 0.6 }}>Request Headers</Typography>
            {["Accept", "Cache-Control", "Connection"].map((h) => (
              <Box key={h} sx={{ display: "flex", gap: 1, fontSize: 10, opacity: 0.4, py: 0.25 }}>
                <Box sx={{ width: 90, fontWeight: 700 }}>{h}</Box>
                <Box>…</Box>
              </Box>
            ))}
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.4,
                p: 0.6,
                borderRadius: 1,
                bgcolor: theme.palette.mode === "dark" ? "rgba(46,160,67,0.18)" : "rgba(46,160,67,0.12)",
                border: "1.5px solid #2ea043",
              })}
            >
              <Box sx={{ width: 90, fontWeight: 800, fontSize: 10.5, color: "#2ea043" }}>Cookie</Box>
              <Box
                sx={{
                  flex: 1,
                  fontSize: 10,
                  fontFamily: "monospace",
                  color: "#2ea043",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                ASP.NET_SessionId=...; .PROMETEO=...; NLB_JusticiaCordoba=...; TS99ec...=...
              </Box>
              <ContentCopyRoundedIcon sx={{ fontSize: 13, color: "#2ea043", flexShrink: 0 }} />
            </Box>
            <Typography sx={{ fontSize: 10, opacity: 0.55, mt: 0.8 }}>
              Seleccioná ese valor completo (todo, de punta a punta) y copialo — eso es lo que va en
              el campo de abajo.
            </Typography>
            <Typography sx={{ fontSize: 10.5, opacity: 0.8, mt: 0.8, fontWeight: 700 }}>
              ⚠️ Fijate que en ese valor aparezca <code>.PROMETEO=...</code> — es la cookie que
              realmente importa. Si no está, recargá la página del portal (F5) y volvé a copiar el
              Cookie de nuevo antes de pegarlo en jex.
            </Typography>
          </Box>
        </MockFrame>
      </StepFrame>

      <StepFrame n={6} title="Si jex dice que la sesión no funciona, repetí desde el paso 3">
        <MockFrame>
          <Box sx={{ p: 1.4 }}>
            <Typography sx={{ fontSize: 10.5, opacity: 0.7 }}>
              A veces la primera cookie que aparece recién logueado todavía no sirve.
              Volvé a la pestaña del portal, <strong>recargá (F5)</strong>, hacé click en un
              pedido nuevo y copiá el Cookie de nuevo — no hace falta volver a loguearte.
            </Typography>
          </Box>
        </MockFrame>
      </StepFrame>
    </Box>
  );
}
